'use client';

import { useCallback, useEffect } from 'react';
import { useSetState } from './useSetState';
import { NoopType } from '@/ts/types';
import { isWindow } from '@/helpers/common';

type IUseBreakpointsProps<
  TMin extends number,
  TMax extends number,
  G extends number,
  F extends number,
  TMin_Max extends [G, F],
> = {
  min?: TMin[];
  max?: TMax[];
  minMax?: TMin_Max[];
};

const matchMedia = (query: string) =>
  isWindow() ? window?.matchMedia(query) : ({ matches: false } as MediaQueryList);

const minMedia = (value: number) => matchMedia(`(min-width: ${value}px)`);
const maxMedia = (value: number) => matchMedia(`(max-width: ${value}px)`);
const minMaxMedia = ([min, max]: [number, number]) =>
  matchMedia(`(min-width: ${min}px) and (max-width: ${max}px)`);

const useBreakpoints = <
  TMin extends number,
  TMax extends number,
  G extends number,
  F extends number,
  TMin_Max extends [G, F],
>({
  min = [],
  max = [],
  minMax = [],
}: IUseBreakpointsProps<TMin, TMax, G, F, TMin_Max>) => {
  const [state, setState] = useSetState<{
    min?: boolean[];
    max?: boolean[];
    minMax?: boolean[];
  }>({
    min: min.map((value) => minMedia(value).matches),
    max: max.map((value) => maxMedia(value).matches),
    minMax: minMax.map((value) => minMaxMedia(value).matches),
  });

  type Parameters<T = void> = (
    type: 'min' | 'max' | 'minMax',
    index: number,
    media: MediaQueryList,
  ) => T;

  const setMatch: Parameters = useCallback(
    (type, index, media) => {
      setState((prev) => {
        const values = prev?.[type] ?? [];
        values[index] = media.matches;

        return { ...prev, [type]: values };
      });
    },
    [setState],
  );

  const listener: Parameters<NoopType> = useCallback(
    (...args) =>
      () =>
        setMatch(...args),
    [setMatch],
  );

  useEffect(() => {
    const minInstances = min.map((number, index) => {
      const media = minMedia(number);
      media?.addEventListener?.('change', listener('min', index, media));
      return () => media?.removeEventListener?.('change', listener('min', index, media));
    });
    const maxInstances = max.map((number, index) => {
      const media = maxMedia(number);

      media?.addEventListener?.('change', listener('max', index, media));
      return () => media?.removeEventListener?.('change', listener('max', index, media));
    });
    const minMaxInstances = minMax.map((value, index) => {
      const media = minMaxMedia(value);
      media?.addEventListener?.('change', listener('minMax', index, media));
      return () => media?.removeEventListener?.('change', listener('minMax', index, media));
    });

    return () => {
      minInstances.forEach((f) => f());
      maxInstances.forEach((f) => f());
      minMaxInstances.forEach((f) => f());
    };
  }, [listener, max, min, minMax]);

  const getIsMin = (value: TMin) => {
    const index = min.findIndex((currentValue) => value === (currentValue as TMin));

    return state?.min?.[index] ?? false;
  };
  const getIsMax = (value: TMax) => {
    const index = max.findIndex((currentValue) => value === (currentValue as TMax));

    return state?.max?.[index] ?? false;
  };
  const getIsMinMax = ([min, max]: TMin_Max) => {
    const index = minMax.findIndex(
      ([currentMin, currentMax]) => min === currentMin && max === currentMax,
    );

    return state?.minMax?.[index] ?? false;
  };

  return { getIsMax, getIsMin, getIsMinMax };
};

export default useBreakpoints;
